import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Banner } from 'ts-core';
import { useBannersV2 } from '../hooks/useBannersV2';
import { useMapBox } from './BalloonEventsMapBox';
import { useBalloonEventsMetadata, useQuery } from 'react-ui';
import { useBalloonEventsData } from '../contexts/EventData';
import { useApplyTagFilter } from '../hooks/useApplyTagFilter';
import { trackMixpanel } from '../utils/mixpanel';

const DESKTOP_SIZE = { width: 80, height: 50 };
const MOBILE_SIZE = { width: 80, height: 50 };
const IMPRESSION_VISIBILITY_THRESHOLD = 0.5;

type BannerVariant = 'desktop' | 'mobile';

interface BannerContainerV2Props {
  variant: BannerVariant;
  className?: string;
  style?: React.CSSProperties;
  /** Called when the count of visible banners changes (e.g. so the host can
   *  resize a bottom sheet to keep adjacent content visible). */
  onVisibleCountChange?: (count: number) => void;
}

function getBannerImageUrl(banner: Banner, variant: BannerVariant): string | null {
  return variant === 'desktop' ? banner.imageDesktopUrl : banner.imageMobileUrl;
}

export function BannerContainerV2({ variant, className, style, onVisibleCountChange }: BannerContainerV2Props) {
  const mapBoxInfo = useMapBox();
  const map = mapBoxInfo?.map ?? null;
  const { addQuery, has, queries } = useQuery();
  const activeTagSlugs = useMemo(
    () => queries.flatMap((q) => q.tags ?? []),
    [queries]
  );

  const { banners, repository } = useBannersV2(map, activeTagSlugs);
  const { ensureLoad, selectEvent, setOpenedEvent, openedEvent } = useBalloonEventsData();
  const { metadata } = useBalloonEventsMetadata();
  const applyTag = useApplyTagFilter();
  const navigate = useNavigate();

  const visibleBanners = useMemo(
    () => banners.filter((b) => getBannerImageUrl(b, variant)),
    [banners, variant]
  );

  useEffect(() => {
    onVisibleCountChange?.(visibleBanners.length);
  }, [visibleBanners.length, onVisibleCountChange]);

  const isBannerActive = useCallback((banner: Banner): boolean => {
    switch (banner.link.type) {
      case 'balloon':
        if (!openedEvent) return false;
        return (
          (banner.link.id != null && openedEvent.id === banner.link.id) ||
          (banner.link.slug != null && openedEvent.slug === banner.link.slug)
        );
      case 'tag':
        return banner.link.slug != null && activeTagSlugs.includes(banner.link.slug);
      case 'category': {
        if (!metadata || !banner.link.slug) return false;
        const category = metadata.getCategoryBySlug(banner.link.slug);
        return category != null && has(category.id);
      }
      default:
        return false;
    }
  }, [activeTagSlugs, has, metadata, openedEvent]);

  // Track which banners we've already recorded an impression for this session.
  const recordedImpressionsRef = useRef<Set<string>>(new Set());
  const buttonRefs = useRef<Map<string, HTMLButtonElement | null>>(new Map());

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const bannerId = (entry.target as HTMLElement).dataset.bannerId;
          if (!bannerId) return;
          if (recordedImpressionsRef.current.has(bannerId)) return;

          const banner = visibleBanners.find((b) => b.id === bannerId);
          if (!banner) return;

          recordedImpressionsRef.current.add(bannerId);
          void repository.recordImpression(bannerId).catch(() => {
            // Swallow — analytics best-effort, retry on next mount.
            recordedImpressionsRef.current.delete(bannerId);
          });
          trackMixpanel('Banner', 'Impression', {
            bannerId,
            bannerTitle: banner.title,
            linkType: banner.link.type,
            variant,
          });
        });
      },
      { threshold: IMPRESSION_VISIBILITY_THRESHOLD }
    );

    visibleBanners.forEach((banner) => {
      const el = buttonRefs.current.get(banner.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [visibleBanners, repository, variant]);

  const performBannerAction = useCallback(async (banner: Banner) => {
    if (banner.link.type === 'balloon') {
      const identifier = banner.link.slug || banner.link.id;
      if (!identifier) return;

      try {
        const event = await ensureLoad(identifier);
        if (!event) return;

        if (map && event.location?.lng != null && event.location?.lat != null) {
          map.flyTo({
            center: [event.location.lng, event.location.lat],
            zoom: 15,
            duration: 1500,
          });
        }

        selectEvent(event.id);
        setOpenedEvent(event);
      } catch (error) {
        console.error('[BannerContainerV2] Failed to load event for balloon action:', error);
      }
      return;
    }

    if (banner.link.type === 'category') {
      if (!metadata) return;

      const category = banner.link.slug
        ? metadata.getCategoryBySlug(banner.link.slug)
        : null;

      if (!category || has(category.id)) return;

      addQuery({ id: category.id, categories: [category.id] });
      return;
    }

    if (banner.link.type === 'tag') {
      const slug = banner.link.slug;
      if (!slug) return;
      // Shared hook: also folds in the tag-bound saved search (forceMapClustering,
      // amount, ...) so clicking a tag banner behaves like /tags/:slug and the
      // SearchBar dropdown — instead of silently skipping the merge.
      void applyTag(slug, { skipIfExists: true });
      return;
    }

    // Fallback: deep link or external URL (used by 'standalone' and any unhandled types)
    if (banner.deepLinkUrl) {
      window.dispatchEvent(new CustomEvent('balloonevents:deeplink', { detail: banner.deepLinkUrl }));
      return;
    }

    if (banner.externalUrl) {
      window.open(banner.externalUrl, '_blank', 'noopener,noreferrer');
    }
  }, [addQuery, ensureLoad, has, map, metadata, selectEvent, setOpenedEvent, applyTag]);

  const handleBannerClick = useCallback((banner: Banner) => {
    void repository.recordClick(banner.id).catch(() => {
      // Swallow — analytics best-effort.
    });
    trackMixpanel('Banner', 'Click', {
      bannerId: banner.id,
      bannerTitle: banner.title,
      linkType: banner.link.type,
      variant,
    });

    // Partner banners open the partner profile experience (scoped map when the
    // partner has events, otherwise just the detail pane over the generic list).
    if (banner.link.type === 'partner' && banner.link.partnerCode) {
      navigate(`/partner/${banner.link.partnerCode}`);
      return;
    }

    void performBannerAction(banner);
  }, [navigate, performBannerAction, repository, variant]);

  const size = variant === 'desktop' ? DESKTOP_SIZE : MOBILE_SIZE;

  if (visibleBanners.length === 0) return null;

  return (
      <div className={className} style={style}>
        {visibleBanners.map((banner) => {
          const imageUrl = getBannerImageUrl(banner, variant);
          if (!imageUrl) return null;
          const borderClass = isBannerActive(banner) ? 'border-balloon' : 'border-gray-600';
          return (
            <button
              key={banner.id}
              ref={(el) => {
                if (el) buttonRefs.current.set(banner.id, el);
                else buttonRefs.current.delete(banner.id);
              }}
              data-banner-id={banner.id}
              type="button"
              className={`flexpointer-events-auto bg-transparent p-0 border-1 ${borderClass} cursor-pointer rounded-md w-[${size.width}px] h-[${size.height}px] overflow-hidden`}
              onClick={() => handleBannerClick(banner)}
              aria-label={banner.title || 'Banner'}
            >
              <img
                src={imageUrl}
                alt={banner.title || ''}
                width={size.width}
                height={size.height}
                style={{ maxHeight: `${size.height}px`, maxWidth: `${size.width}px` }}
                className={`object-cover rounded-md w-[${size.width}px] h-[${size.height}px] max-h-[${size.height}px]`}
              />
            </button>
          );
        })}
      </div>
  );
}
